/**
 * Lightweight in-memory query cache with in-flight deduplication.
 * Phase A substitute for TanStack Query — no extra dependency.
 */

const cache = new Map();
const inflight = new Map();

export function queryFetch(key, fetcher, { ttlMs = 15000, force = false } = {}) {
  if (!force) {
    const cached = cache.get(key);
    if (cached && Date.now() - cached.ts < ttlMs) {
      return Promise.resolve(cached.data);
    }
    if (inflight.has(key)) {
      return inflight.get(key);
    }
  } else {
    cache.delete(key);
    inflight.delete(key);
  }

  const promise = Promise.resolve()
    .then(fetcher)
    .then((data) => {
      cache.set(key, { data, ts: Date.now() });
      inflight.delete(key);
      return data;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, promise);
  return promise;
}

export function peekQueryCache(key, ttlMs = Infinity) {
  const cached = cache.get(key);
  if (!cached) return null;
  if (ttlMs !== Infinity && Date.now() - cached.ts >= ttlMs) return null;
  return cached.data;
}

export function hasQueryCache(key, ttlMs = Infinity) {
  const cached = cache.get(key);
  if (!cached) return false;
  if (ttlMs !== Infinity && Date.now() - cached.ts >= ttlMs) return false;
  return true;
}

export function seedQueryCache(key, data) {
  // Allow explicit empty arrays (known-empty after convert) — only skip null/undefined
  if (data == null) return;
  cache.set(key, { data, ts: Date.now() });
}

export function invalidateQueries(keyPrefix) {
  for (const key of cache.keys()) {
    if (key === keyPrefix || key.startsWith(`${keyPrefix}:`)) {
      cache.delete(key);
    }
  }
  for (const key of inflight.keys()) {
    if (key === keyPrefix || key.startsWith(`${keyPrefix}:`)) {
      inflight.delete(key);
    }
  }
}
